import { formatCurrency } from '@/lib/utils';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { CheckCircle2, RotateCcw, Plus, Trash2, CalendarRange, Scale, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format, endOfMonth, startOfMonth, subMonths, parseISO } from 'date-fns';

const formatDate = (d) => { try { return format(parseISO(d), 'dd MMM yyyy'); } catch { return d; } };

const defaultForm = (nextStart) => {
  const base = nextStart ? parseISO(nextStart) : startOfMonth(subMonths(new Date(), 1));
  return {
    name: format(base, 'MMMM yyyy'),
    start_date: format(base, 'yyyy-MM-dd'),
    end_date: format(endOfMonth(base), 'yyyy-MM-dd'),
    notes: ''
  };
};

const NewPeriodDialog = ({ open, onOpenChange, onCreate, saving, nextStart }) => {
  const [form, setForm] = useState(defaultForm(nextStart));
  useEffect(() => { if (open) setForm(defaultForm(nextStart)); }, [open, nextStart]);
  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-period-dialog">
        <DialogHeader>
          <DialogTitle>New Reconciliation Period</DialogTitle>
          <DialogDescription>
            {nextStart ? `Starts the day after the previous period ends (${formatDate(nextStart)}).` : 'This is the first period. Pick the date the recon chain starts from.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input value={form.name} onChange={set('name')} data-testid="period-name-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start date</Label>
              <Input type="date" value={form.start_date} onChange={set('start_date')} disabled={!!nextStart} data-testid="period-start-input" />
            </div>
            <div><Label>End date</Label><Input type="date" value={form.end_date} min={form.start_date} onChange={set('end_date')} data-testid="period-end-input" /></div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={set('notes')} placeholder="Optional" data-testid="period-notes-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onCreate(nextStart ? { ...form, start_date: undefined } : form)} disabled={saving} className="bg-[#006241] hover:bg-[#004d33]" data-testid="period-create-submit">
            {saving ? 'Creating...' : 'Create Period'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PeriodRow = ({ period, onReconcile, onAction }) => {
  const reconciled = period.status === 'reconciled';
  const s = reconciled && period.reconciled_snapshot ? period.reconciled_snapshot : period.current;
  return (
    <TableRow data-testid={`period-row-${period.id}`}>
      <TableCell className="font-medium">
        {period.name}
        {period.notes && <div className="text-xs text-muted-foreground">{period.notes}</div>}
      </TableCell>
      <TableCell className="text-sm">{formatDate(period.start_date)} – {formatDate(period.end_date)}</TableCell>
      <TableCell className="text-right font-mono">{formatCurrency(s.opening_balance)}</TableCell>
      <TableCell className="text-right font-mono text-green-700">+{formatCurrency(s.total_in)}</TableCell>
      <TableCell className="text-right font-mono text-red-700">{s.total_out ? '-' : ''}{formatCurrency(s.total_out)}</TableCell>
      <TableCell className="text-right font-mono" data-testid={`period-closing-${period.id}`}>{formatCurrency(s.closing_balance)}</TableCell>
      <TableCell className="text-right font-mono font-medium" data-testid={`period-ending-${period.id}`}>
        {reconciled && period.ending_balance != null ? formatCurrency(period.ending_balance) : '-'}
      </TableCell>
      <TableCell>
        {reconciled ? (
          <Badge className="bg-[#006241] text-white" data-testid={`period-status-${period.id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Reconciled</Badge>
        ) : (
          <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50" data-testid={`period-status-${period.id}`}>Open</Badge>
        )}
        {reconciled && <div className="text-xs text-muted-foreground mt-1">by {period.reconciled_by} · {period.reconciled_at ? format(new Date(period.reconciled_at), 'dd MMM yyyy') : ''}</div>}
      </TableCell>
      <TableCell>
        <div className="flex justify-end gap-1">
          {reconciled ? (
            <Button variant="ghost" size="sm" onClick={() => onReconcile(period)} data-testid={`period-view-${period.id}`}>
              <Eye className="h-4 w-4 mr-1" />View
            </Button>
          ) : (
            <Button size="sm" className="bg-[#006241] hover:bg-[#004d33]" onClick={() => onReconcile(period)} data-testid={`period-reconcile-${period.id}`}>
              <Scale className="h-4 w-4 mr-1" />Reconcile
            </Button>
          )}
          {period.can_reopen && (
            <Button variant="outline" size="sm" onClick={() => onAction('reopen', period)} data-testid={`period-reopen-${period.id}`}>
              <RotateCcw className="h-4 w-4 mr-1" />Reopen
            </Button>
          )}
          {period.can_delete && (
            <Button variant="ghost" size="icon" className="text-red-600" onClick={() => onAction('delete', period)} data-testid={`period-delete-${period.id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

const ACTION_COPY = {
  reopen: { title: 'Reopen this period?', body: 'Transactions in this period can be changed again. Reconcile it again once you have finished.', cta: 'Reopen Period' },
  delete: { title: 'Delete this period?', body: 'The period is removed. No transactions are affected.', cta: 'Delete' }
};

export default function PeriodsPage() {
  const { api } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [businessId, setBusinessId] = useState('');
  const [data, setData] = useState({ periods: [], next_start_date: null, can_create: true });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api-keys/all').then(res => {
      setBusinesses(res.data);
      if (res.data.length) setBusinessId(res.data[0].id);
    }).catch(() => toast.error('Failed to load businesses')).finally(() => setLoading(false));
  }, [api]);

  const fetchPeriods = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await api.get('/periods', { params: { business_id: businessId } });
      setData(res.data);
    } catch {
      toast.error('Failed to load periods');
    }
  }, [api, businessId]);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await api.post('/periods', { ...form, business_id: businessId });
      toast.success('Period created');
      setDialogOpen(false);
      fetchPeriods();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create period');
    } finally {
      setSaving(false);
    }
  };

  const runAction = async () => {
    const { action, period } = pending;
    setPending(null);
    try {
      const res = action === 'delete' ? await api.delete(`/periods/${period.id}`) : await api.post(`/periods/${period.id}/${action}`);
      toast.success(res.data.message);
      fetchPeriods();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action} period`);
    }
  };

  if (loading) {
    return <div className="page-container"><Skeleton className="h-10 w-64 mb-6" /><Skeleton className="h-96" /></div>;
  }

  const copy = pending ? ACTION_COPY[pending.action] : null;

  return (
    <div className="page-container animate-slide-in" data-testid="periods-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A18]">Reconciliation Periods</h1>
          <p className="text-muted-foreground text-sm mt-1">Each period follows on from the last. Tick every line and match the Xero/Zoho and counted cash balances to reconcile; reconciled periods are locked.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={businessId} onValueChange={setBusinessId}>
            <SelectTrigger className="w-56" data-testid="period-business-select"><SelectValue placeholder="Select business" /></SelectTrigger>
            <SelectContent>
              {businesses.map(b => <SelectItem key={b.id} value={b.id}>{b.business_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            onClick={() => setDialogOpen(true)}
            disabled={!data.can_create}
            title={data.can_create ? '' : 'Reconcile the current period first'}
            className="bg-[#006241] hover:bg-[#004d33]"
            data-testid="new-period-btn"
          >
            <Plus className="h-4 w-4 mr-1" />New Period
          </Button>
        </div>
      </div>

      <Card data-testid="periods-table">
        <CardContent className="p-0">
          {data.periods.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CalendarRange className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-[#1A1A18]">No periods yet</h3>
              <p className="text-muted-foreground mt-1">Create your first period, enter the counted ending balance, then reconcile it</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F2F2F0]">
                    <TableHead>Period</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead className="text-right">Opening</TableHead>
                    <TableHead className="text-right">In</TableHead>
                    <TableHead className="text-right">Out</TableHead>
                    <TableHead className="text-right">System Closing</TableHead>
                    <TableHead className="text-right">Ending Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.periods.map(p => (
                    <PeriodRow key={p.id} period={p} onReconcile={(per) => navigate(`/periods/${per.id}`)} onAction={(action, period) => setPending({ action, period })} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <NewPeriodDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreate={handleCreate} saving={saving} nextStart={data.next_start_date} />

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="period-action-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
            <AlertDialogDescription>{pending?.period.name}: {copy?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runAction} data-testid="period-action-confirm">{copy?.cta}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
